import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import Icon, { Icons } from '../../components/Icon/Icon';
import Input from '../../components/Input';
import PageHeader from '../../components/PageHeader';
import { t } from '../../lang';
import HelperService from '../../services/HelperService';
import RequestService from '../../services/RequestService';
import { appSlice } from '../../store/slices';

import './ConfirmCaptcha.scss';

const LOADING_ICON_SIZE = 24;

const ConfirmCaptcha = () => {
	const [captchaId, setCaptchaId] = useState();
	const [captchaImg, setCaptchaImg] = useState();
	const [captchaCode, setCaptchaCode] = useState('');
	const [captchaLoading, setCaptchaLoading] = useState(false);
	const [submitLoading, setSubmitLoading] = useState(false);

	const captchaConfirmRequestData = useSelector(state => state.app.captchaConfirmRequestData);

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const intl = useIntl();

	const updateCaptchaCode = e => {
		setCaptchaCode(e.target.value);
	};

	const getCaptchaData = async () => {
		try {
			setCaptchaLoading(true);
			setCaptchaId();
			setCaptchaImg();
			setCaptchaCode('');
			const response = await RequestService.CreateCaptcha();

			if (response.Status === 'Success') {
				setCaptchaId(response.Id);
				setCaptchaImg(response.Image);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setCaptchaLoading(false);
		}
	};

	const confirmCaptcha = async () => {
		try {
			setSubmitLoading(true);
			const response = await RequestService.RegulareRequest(
				captchaConfirmRequestData.endpoint,
				{ ...captchaConfirmRequestData.data, captchaId, captchaCode },
				captchaConfirmRequestData.handleError
			);
			if (response.Status === 'Success') {
				captchaConfirmRequestData.resultHandler(response);
			} else {
				if (response.ErrorCode === 'CaptchaValidationError') {
					getCaptchaData();
					HelperService.showNotification({
						type: 'error',
						title: t('Invalid code.'),
						description: t('Please try again.'),
						icon: 'EXCLAMATIONMARK_TRIANGLE',
					});
				} else {
					captchaConfirmRequestData.resultHandler(response);
					if (response.ErrorCode !== 'DeviceNotFound') navigate(-1);
					dispatch(appSlice.actions.setCaptchaConfirmRequestData());
				}
			}
		} catch (error) {
			getCaptchaData();
			captchaConfirmRequestData.failHandler(error);
		} finally {
			setSubmitLoading(false);
		}
	};

	useEffect(() => {
		getCaptchaData();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className='ConfirmCaptcha'>
			<PageHeader header={location.state?.captchaPageHeader || ''} subheader={'Enter the characters you see'} />
			<div className='ConfirmCaptcha__body'>
				<div className='ConfirmCaptcha__captcha-image-box FormBlock'>
					{captchaLoading ? (
						<Icon type={Icons.LOADING_SPINNER} width={LOADING_ICON_SIZE} height={LOADING_ICON_SIZE} />
					) : (
						captchaImg && <img src={'data:image/png;base64,' + captchaImg} alt='Captcha Image' />
					)}
				</div>

				<div className='ConfirmCaptcha__refresh-block'>
					<span>{t('Can’t read?')}</span>
					<Button className='ConfirmCaptcha__refresh-button' styleType='ghost' onClick={getCaptchaData}>
						{t('Refresh')}
					</Button>
				</div>
				<Input
					className='ConfirmCaptcha__captcha-field FormBlock'
					value={captchaCode}
					onChange={updateCaptchaCode}
					placeholder={intl.formatMessage({ id: 'Enter the CAPTCHA code' })}
					maxLength={10}
					autoFocus
				/>
				<Button
					onClick={confirmCaptcha}
					disabled={(!captchaCode && !submitLoading) || captchaLoading}
					loading={submitLoading}
				>
					{t('Submit')}
				</Button>
			</div>
		</div>
	);
};

export default ConfirmCaptcha;
